/* eslint-disable @typescript-eslint/no-explicit-any */
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { skipErrorDialog } from '@ct/data';
import { PROJECT_PREFIX } from '@ct/model';
import { ErrorHandlerService } from '@ngx-stoui/error-handler';
import {
  catchError,
  filter,
  interval,
  map,
  mergeWith,
  Observable,
  of,
  ReplaySubject,
  startWith,
  Subject,
  switchMap,
  take,
} from 'rxjs';
import { ReleaseNoteDto } from './models';

@Injectable()
export class ReleaseNotesService {
  private http = inject(HttpClient);
  private proj = inject(PROJECT_PREFIX);
  private errorHandler = inject(ErrorHandlerService);

  private ctPath = this.proj + 'ct';
  private releaseNotes$ = new ReplaySubject<ReleaseNoteDto>();
  private update$ = new Subject<void>();
  private handleError = catchError<any, any>((err: HttpErrorResponse) => {
    this.errorHandler.handler(err);
    return of(null);
  });

  getReleaseNotesDto() {
    return this.releaseNotes$.asObservable();
  }
  getReleaseNotes() {
    return this.getReleaseNotesDto().pipe(map((r) => r?.releaseNotes ?? []));
  }
  getFirstFive() {
    return this.getReleaseNotes().pipe(
      map((r) => {
        const unread = r.filter((x) => !x.read);
        const rest = r.filter((x) => !unread.includes(x));
        return [...unread, ...rest].slice(0, 5);
      }),
    );
  }
  getUnreadCounter() {
    return this.getReleaseNotes().pipe(
      filter((r) => !!r),
      map((r) => r.filter((r) => !r.read).length),
    );
  }

  list(): Observable<ReleaseNoteDto> {
    return this.http
      .get<ReleaseNoteDto>(`/${this.ctPath}/userconfig/releasenotes/all`, { context: skipErrorDialog() })
      .pipe(this.handleError);
  }

  markAllRead() {
    this.releaseNotes$
      .pipe(
        take(1),
        switchMap((r) =>
          this.http.put(`/${this.ctPath}/userconfig/releasenotes/mark-all-read`, null).pipe(this.handleError),
        ),
      )
      .subscribe(() => this.update$.next());
  }

  constructor() {
    const loader$ = this.update$.pipe(
      mergeWith([
        interval(60_000) // poll once every minute
          .pipe(startWith(0)),
      ]),
    );
    loader$ // poll once every minute
      .pipe(switchMap(() => this.list()))
      .subscribe((r) => this.releaseNotes$.next(r));
  }
}
